import React, { useState } from 'react';
import { giftPoints, getUserBalance, GiftPointsRequest } from '../../api/user-admin';

const GiftPoints = () => {
    const [formData, setFormData] = useState({
        targetUserId: '',
        points: '',
        reason: 'lost points'
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [userBalance, setUserBalance] = useState<number | null>(null);

    const reasonOptions = [
        { value: 'lost points', label: 'Lost Points' },
        { value: 'found bug', label: 'Found Bug' },
        { value: 'compensation', label: 'Compensation' },
        { value: 'bonus', label: 'Bonus' },
        { value: 'other', label: 'Other' }
    ];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        setError('');
        setSuccess('');
    };

    const handleCheckBalance = async () => {
        if (!formData.targetUserId.trim()) {
            setError('Please enter a user ID');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const response = await getUserBalance(formData.targetUserId.trim());
            setUserBalance(response.balance);
            setSuccess(`Current balance for user ${formData.targetUserId}: ${response.balance} points`);
        } catch (err: any) {
            setError(err.message);
            setUserBalance(null);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.targetUserId.trim() || !formData.points.trim()) {
            setError('Please fill in all fields');
            return;
        }

        const points = parseInt(formData.points);
        if (isNaN(points) || points <= 0) {
            setError('Points must be a positive number');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const request: GiftPointsRequest = {
                targetUserId: formData.targetUserId.trim(),
                points: points,
                reason: formData.reason
            };

            const response = await giftPoints(request);
            setSuccess(`Successfully gifted ${response.pointsGifted} points to user ${response.targetUserId}. New balance: ${response.newBalance} points`);
            setUserBalance(response.newBalance);
            
            // Reset form
            setFormData({
                targetUserId: formData.targetUserId,
                points: '',
                reason: formData.reason
            });
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto py-8">
            <div className="bg-white shadow-lg rounded-lg p-6">
                <h2 className="text-2xl font-bold mb-6 text-gray-800">Gift Points to User</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label htmlFor="targetUserId" className="block text-sm font-medium text-gray-700 mb-2">
                            User ID
                        </label>
                        <div className="flex space-x-2">
                            <input
                                type="text"
                                id="targetUserId"
                                name="targetUserId"
                                value={formData.targetUserId}
                                onChange={handleChange}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Enter user ID"
                                disabled={loading}
                            />
                            <button
                                type="button"
                                onClick={handleCheckBalance}
                                disabled={loading || !formData.targetUserId.trim()}
                                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Check Balance
                            </button>
                        </div>
                    </div>

                    {userBalance !== null && (
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                            <p className="text-sm text-blue-800">
                                Current balance: <span className="font-semibold">{userBalance} points</span>
                            </p>
                        </div>
                    )}

                    <div>
                        <label htmlFor="points" className="block text-sm font-medium text-gray-700 mb-2">
                            Points to Gift
                        </label>
                        <input
                            type="number"
                            id="points"
                            name="points"
                            value={formData.points}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter number of points"
                            min="1"
                            disabled={loading}
                        />
                    </div>

                    <div>
                        <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
                            Reason
                        </label>
                        <select
                            id="reason"
                            name="reason"
                            value={formData.reason}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={loading}
                        >
                            {reasonOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3">
                            <p className="text-sm text-red-800">{error}</p>
                        </div>
                    )}

                    {success && (
                        <div className="bg-green-50 border border-green-200 rounded-md p-3">
                            <p className="text-sm text-green-800">{success}</p>
                        </div>
                    )}

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Gifting Points...' : 'Gift Points'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default GiftPoints;
